import React, { useMemo } from 'react';
import { Download, Lock, ExternalLink } from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import type { AggregatedClientDocument } from '../../types/aggregated-documents';
import { aggregatedDocumentsService } from '../../services/aggregated-documents-service';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { parseDbTimestamp } from '../../utils/datetime';
import { DocumentDescription } from './DocumentDescription';
import { DataTable } from '@/components/ui/data-table';

interface FlatDocumentsViewProps {
  documents: AggregatedClientDocument[];
  selectedDocs: Set<string>;
  onToggleSelect: (docId: string) => void;
  onDownload: (doc: AggregatedClientDocument) => void;
  onPreview: (doc: AggregatedClientDocument) => void;
  /** Grava a finalidade do documento (null apaga); sem ela, só exibe. */
  getDescriptionSaver?: (doc: AggregatedClientDocument) => ((description: string | null) => Promise<void>) | undefined;
}

export const FlatDocumentsView: React.FC<FlatDocumentsViewProps> = ({
  documents,
  selectedDocs,
  onToggleSelect,
  onDownload,
  onPreview,
  getDescriptionSaver,
}) => {
  const allSelected = documents.length > 0 && selectedDocs.size === documents.length;

  const columns = useMemo<ColumnDef<AggregatedClientDocument>[]>(() => [
    {
      id: 'select',
      enableSorting: false,
      header: () => (
        <input
          type="checkbox"
          checked={allSelected}
          onChange={() => {
            if (allSelected) {
              documents.forEach((doc) => {
                if (selectedDocs.has(doc.id)) onToggleSelect(doc.id);
              });
            } else {
              documents.forEach((doc) => {
                if (!selectedDocs.has(doc.id)) onToggleSelect(doc.id);
              });
            }
          }}
          className="size-4 rounded-sm border-input accent-primary"
          aria-label="Selecionar todos"
        />
      ),
      cell: ({ row }) => (
        <input
          type="checkbox"
          checked={selectedDocs.has(row.original.id)}
          onChange={() => onToggleSelect(row.original.id)}
          className="size-4 rounded-sm border-input accent-primary"
          aria-label="Selecionar documento"
        />
      ),
    },
    {
      accessorKey: 'fileName',
      header: 'Nome do Arquivo',
      cell: ({ row }) => {
        const doc = row.original;
        const icon = aggregatedDocumentsService.getFileIcon(doc.mimeType);
        return (
          <div className="flex items-center">
            <span className="text-xl mr-2">{icon}</span>
            <div>
              <button
                onClick={() => onPreview(doc)}
                className="flex min-w-0 items-center text-sm font-medium hover:text-primary"
              >
                {doc.fileName}
                {doc.isConfidential && (
                  <Lock className="ml-2 size-3 shrink-0 text-muted-foreground" aria-label="Confidencial" />
                )}
              </button>
              <DocumentDescription
                name={doc.fileName}
                description={doc.description}
                onSave={getDescriptionSaver?.(doc)}
                className="max-w-xs text-xs"
              />
              <div className="text-xs text-muted-foreground truncate max-w-xs">
                {doc.sourceEntityName}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: 'sourceType',
      header: 'Origem',
      cell: ({ row }) => (
        <div className="flex items-center">
          <span className="text-lg mr-1">
            {aggregatedDocumentsService.getSourceTypeIcon(row.original.sourceType)}
          </span>
          <span className="text-sm text-foreground">
            {aggregatedDocumentsService.getSourceTypeLabel(row.original.sourceType)}
          </span>
        </div>
      ),
    },
    {
      accessorKey: 'documentType',
      header: 'Tipo',
      cell: ({ row }) => (
        <span className="inline-flex rounded-full bg-secondary px-2 text-xs font-medium leading-5 text-secondary-foreground">
          {row.original.documentType}
        </span>
      ),
    },
    {
      accessorKey: 'fileSize',
      header: 'Tamanho',
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">
          {aggregatedDocumentsService.formatFileSize(row.original.fileSize)}
        </span>
      ),
    },
    {
      accessorKey: 'uploadedAt',
      header: 'Data',
      cell: ({ row }) => {
        const uploadedAt = parseDbTimestamp(row.original.uploadedAt);
        return (
          <span className="text-sm text-muted-foreground">
            {uploadedAt ? format(uploadedAt, 'dd/MM/yyyy', { locale: ptBR }) : '-'}
          </span>
        );
      },
    },
    {
      accessorKey: 'uploadedByName',
      header: 'Enviado por',
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">{row.original.uploadedByName}</span>
      ),
    },
    {
      id: 'actions',
      header: () => <span className="sr-only">Ações</span>,
      enableSorting: false,
      cell: ({ row }) => (
        <div className="text-right">
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={() => onPreview(row.original)}
                className="mr-3 text-muted-foreground hover:text-foreground"
                aria-label="Visualizar"
              >
                <ExternalLink className="h-4 w-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent>Visualizar</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={() => onDownload(row.original)}
                className="text-muted-foreground hover:text-foreground"
                aria-label="Baixar"
              >
                <Download className="h-4 w-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent>Baixar</TooltipContent>
          </Tooltip>
        </div>
      ),
    },
  ], [allSelected, selectedDocs, onToggleSelect, onPreview, onDownload, documents, getDescriptionSaver]);

  if (documents.length === 0) {
    return (
      <div className="rounded-lg border bg-muted/40 py-12 text-center">
        <p className="text-sm text-muted-foreground">Nenhum documento encontrado</p>
      </div>
    );
  }

  return (
    <div className="min-w-0 rounded-lg border bg-card p-2 sm:p-4">
      <DataTable
        columns={columns}
        data={documents}
        minWidth="min-w-[900px]"
        emptyMessage="Nenhum documento encontrado"
        rowClassName={(doc) => (selectedDocs.has(doc.id) ? 'bg-muted' : undefined)}
      />
    </div>
  );
};
